import { TextInput } from '@components/form';
import { Group } from '@components/layout';
import { SortButton } from '@components/sort';
import { Stack, SxProps } from '@mui/material';
import posthog from 'posthog-js';
import { PropsWithChildren, useState } from 'react';
import { useDebounce } from 'react-use';
import { FilterChips } from './FilterChips';
import { FiltersMenu } from './FilterMenu';
import { DeckardFilter, Filter, SearchDetailTypes } from './types';

type SearchAndFilterProps<T> = {
  context: string;
  filter?: DeckardFilter<T>;
  placeholder?: string;
  searchDetail?: SearchDetailTypes;
  sort?: boolean;
  sortOptions?: string[];
  sortValue?: string;
  sortDirection?: 'asc' | 'desc';
  sx?: SxProps;
  variant?: 'primary' | 'secondary';
  setFilter?: (filter: DeckardFilter<T>) => void;
  onSortValueChange?: (value: string) => void;
  onSortDirectionChange?: (direction: 'asc' | 'desc') => void;
};

export const SearchAndFilter = <T,>({
  children,
  context,
  filter,
  placeholder,
  searchDetail,
  sort = false,
  sortOptions,
  sortValue,
  sortDirection,
  sx,
  variant = 'primary',
  setFilter,
  onSortValueChange,
  onSortDirectionChange,
}: PropsWithChildren<SearchAndFilterProps<T>>) => {
  const [searchText, setSearchText] = useState('');
  useDebounce(
    () => {
      const newFilter = {
        ...filter,
        texts: {
          ...(filter as unknown as { texts: Filter<string> }).texts,
          value: searchText ? searchText.trim().split(' ') : [],
        },
      };
      setFilter(newFilter);
      posthog.capture('user_search-and-filter', { type: 'search', context, filter: newFilter });
    },
    400,
    [searchText],
  );

  const clearFilterKey = (key: string) => {
    const newFilter = {
      ...filter,
      [key]: {
        ...(filter[key] as Filter<unknown>),
        value: (filter[key] as Filter<unknown>).type === 'boolean' ? false : [],
        display: [],
      },
    };
    setFilter(newFilter);
    posthog.capture('user_search-and-filter', { type: 'clear', context, filter: newFilter });
  };

  const onFilterChange = (newFilter: DeckardFilter<T>) => {
    setFilter(newFilter);
    posthog.capture('user_search-and-filter', { type: 'filter', context, filter: newFilter });
  };

  const updateFilter = (key: string, newValue: unknown) => {
    const newFilter = {
      ...filter,
      [key]: { ...(filter[key] as Filter<unknown>), value: newValue },
    };
    setFilter(newFilter);
    posthog.capture('user_search-and-filter', { type: 'filter', context, filter: newFilter });
  };

  return (
    <Stack gap={2} width="100%" sx={sx}>
      <Group>
        <TextInput
          fullWidth
          sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          placeholder={placeholder ?? 'Search by name, description, or tags'}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {filter && (
          <FiltersMenu
            filter={filter}
            searchDetail={searchDetail}
            sort={sort}
            onFilterChange={onFilterChange}
            variant={variant}
          />
        )}
        {sort && (
          <SortButton
            variant={variant}
            sort={[sortDirection, sortValue]}
            options={sortOptions}
            onSortValueChange={onSortValueChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        )}
        {children}
      </Group>
      <FilterChips
        filter={filter}
        variant={variant}
        clearFilterKey={clearFilterKey}
        updateFilter={updateFilter}
      />
    </Stack>
  );
};
